// src/components/Layout.jsx
// Mirrors the main.asp shell: sticky header with logo + nav menu, 
// Bootstrap 5 modals (confirm, notice, password change), toast notification.
// All content is rendered in the <main> section via {children}.

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTexts, TXT } from '../context/TextContext';
// TXT.TIMEZONE (101) used for user menu item
import { useNavigate } from 'react-router-dom';
import api, { authApi, tabsApi } from '../services/api';
import ConfirmModal from './modals/ConfirmModal';
import PasswordChangeModal from './modals/PasswordChangeModal';
import ConfigModal from './modals/ConfigModal';
import AssistantPanel from './AssistantPanel';

export default function Layout({ children, onNewDocument, summary, buttonBar, sidebar }) {
  const { user, logout } = useAuth();
  const { t, idiId } = useTexts();


  const navigate = useNavigate();
  const [navFilters, setNavFilters] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [mentionCount, setMentionCount] = useState(0);
  const [unreadPopover, setUnreadPopover] = useState(false);
  const [unreadDocs, setUnreadDocs]       = useState([]);
  const unreadRef = useRef(null);
  const [filterBadges, setFilterBadges] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});
  const [showPwdChange, setShowPwdChange] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [empInfo, setEmpInfo] = useState({ name: '', logo: 'logo00.png' });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.classList.add('mobile-nav-active');
    } else {
      document.body.classList.remove('mobile-nav-active');
    }
    return () => document.body.classList.remove('mobile-nav-active');
  }, [mobileNavOpen]);

  const dispatchSearch = (text) => {
    window.dispatchEvent(new CustomEvent('whl:search', { detail: { text } }));
    if (searchRef.current) searchRef.current.value = '';
  };

  useEffect(() => {
    if (user) {
      fetchNavMenu();
      fetchEmpInfo();
      fetchMentionCount();
      // Polling cada 60 segundos
      const interval = setInterval(() => {
        fetchMentionCount();
        fetchFilterBadges(navFilters);
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchMentionCount = () => {
    tabsApi.mentionsCount()
      .then(res => setMentionCount(res.data.count || 0))
      .catch(() => {});
  };

  const openUnreadPopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (unreadPopover) { setUnreadPopover(false); return; }
    tabsApi.mentionsUnread()
      .then(res => { setUnreadDocs(res.data || []); setUnreadPopover(true); })
      .catch(() => {});
  };

  useEffect(() => {
    if (!unreadPopover) return;
    const close = (e) => { if (unreadRef.current && !unreadRef.current.contains(e.target)) setUnreadPopover(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [unreadPopover]);

  const fetchNavMenu = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/filters/menu`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('whl_token')}` } }
      );
      const data = await res.json();
      setNavFilters(data);
      fetchFilterBadges(data);
    } catch (err) {
      console.error('Failed to load nav menu:', err);
    }
  };

  const fetchFilterBadges = async (filters) => {
    const withBadge = (filters || []).filter(f => f.Fil_Badge);
    if (!withBadge.length) return;
    const results = await Promise.all(
      withBadge.map(f =>
        api.get(`/filters/${f.filtroid}/count`)
          .then(r => ({ id: f.filtroid, count: r.data.count || 0 }))
          .catch(() => ({ id: f.filtroid, count: 0 }))
      )
    );
    const map = {};
    results.forEach(r => { map[r.id] = r.count; });
    setFilterBadges(map);
  };

  const fetchEmpInfo = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/emp-info`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('whl_token')}` } }
      );
      const data = await res.json();
      setEmpInfo(data);
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDbBackup = async () => {
    try {
      showToast('Ejecutando backup...');
      await api.post('/admin/db-backup');
      showToast('Backup ejecutado correctamente');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al ejecutar el backup');
    }
  };

  const handleDbLogFree = async () => {
    try {
      showToast('Liberando log de transacciones...');
      await api.post('/admin/db-log-free');
      showToast('Log de transacciones liberado');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al liberar el log');
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3500);
  };

  const openConfirm = (message, onConfirm) => {
    setConfirmConfig({ message, onConfirm });
    setShowConfirm(true);
  };

  // Build nested nav items from flat filter list (Fil_TipoFil=10 means dropdown parent)
  const renderNavItems = () => {
    const items = [];
    let currentDropdown = null;

    navFilters.forEach((f) => {
      if (f.Fil_TipoFil === 10) {
        // Dropdown header
        if (currentDropdown) items.push(currentDropdown);
        currentDropdown = { ...f, children: [] };
      } else if (currentDropdown && f.fil_parent === currentDropdown.filtroid) {
        currentDropdown.children.push(f);
      } else {
        if (currentDropdown) {
          items.push(currentDropdown);
          currentDropdown = null;
        }
        items.push(f);
      }
    });
    if (currentDropdown) items.push(currentDropdown);

    const handleClick = (e, item) => {
      e.preventDefault();
      if (item.Fil_TipoFil === 0) {
        const route = (item.Fil_Link || '').trim();
        if (route) {
          const [pathname, search] = route.split('?');
          navigate({ pathname, search: search ? `?${search}` : '' });
        }
      } else {
        if (window.location.pathname === '/documents') {
          window.dispatchEvent(
            new CustomEvent('whl:filter', { detail: { filterId: item.filtroid, tipoFil: item.Fil_TipoFil } })
          );
        } else {
          navigate('/documents', { state: { filterId: item.filtroid, tipoFil: item.Fil_TipoFil } });
        }
      }
    };

    return items.map((item) => {
      if (item.children) {
        return (
          <li key={item.filtroid} className="dropdown">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              // En mobile, toggle dropdown-active en el <ul> hijo
              if (mobileNavOpen) {
                const li = e.currentTarget.parentElement;
                const ul = li.querySelector('ul');
                if (ul) ul.classList.toggle('dropdown-active');
              }
            }}>
              <span>{item.FIL_DesFil}</span>
              <i className="bi bi-chevron-down toggle-dropdown"></i>
            </a>
            <ul>
              {item.children.map((child) => (
                <li key={child.filtroid}>
                  <a href="#" onClick={(e) => { handleClick(e, child); setMobileNavOpen(false); }}>
                    {child.FIL_DesFil}
                  </a>
                </li>
              ))}
            </ul>
          </li>
        );
      }
      const badgeCount = filterBadges[item.filtroid];
      return (
        <li key={item.filtroid}>
          <a href="#" onClick={(e) => { handleClick(e, item); setMobileNavOpen(false); }}>
            {item.FIL_DesFil}
            {badgeCount > 0 && (
              <span className="badge rounded-pill ms-1"
                style={{ background: '#28a745', color: '#fff', fontSize: '0.65rem', verticalAlign: 'middle' }}>
                {badgeCount}
              </span>
            )}
          </a>
        </li>
      );
    });
  };

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* HEADER - mirrors main.asp <header> */}
      {/* ------------------------------------------------------------------ */}
      <header id="header" className="header d-flex align-items-center sticky-top"
        style={process.env.REACT_APP_ENV === 'test' ? { background: '#d97706', borderBottom: '3px solid #92400e' } : undefined}>
        <div className="container-fluid px-4 position-relative d-flex align-items-center justify-content-between">

          <a href="/" className="logo d-flex align-items-center" onClick={(e) => {
            e.preventDefault();
            const defaultFilterId = navFilters[0]?.filtrodef;
            if (defaultFilterId) {
              if (window.location.pathname === '/documents') {
                window.dispatchEvent(new CustomEvent('whl:filter', { detail: { filterId: defaultFilterId } }));
              } else {
                navigate('/documents', { state: { filterId: defaultFilterId } });
              }
            } else {
              navigate('/documents');
            }
          }}>
            <img src={`/images/${empInfo.logo}`} alt="" />
            <h1 className="sitename">{empInfo.name}</h1>
          </a>

          {user && (
            <nav id="navmenu" className="navmenu" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              {/* Centro: search + filtros */}
              <ul style={{ flex: 1, justifyContent: 'center' }}>
                <li>
                  <input
                    ref={searchRef}
                    type="text"
                    id="topic_search"
                    placeholder={t(TXT.SEARCH, 'Search')}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') dispatchSearch(e.target.value);
                    }}
                  />
                </li>
                {renderNavItems()}
              </ul>

              {/* Derecha: menú de usuario */}
              <ul style={{ flexShrink: 0 }}>
                <li className="dropdown dropdown-end">
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    if (mobileNavOpen) {
                      const li = e.currentTarget.parentElement;
                      const ul = li.querySelector('ul');
                      if (ul) ul.classList.toggle('dropdown-active');
                    }
                  }}>
                    <span>
                      {user.name}&nbsp;&nbsp;
                    </span>
                    <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    {(user?.functions || []).includes(5) && (
                    <li>
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate('/users'); }}>
                        <span><i className="bi bi-people" style={{ marginRight: 4 }} />{t(TXT.USUARIOS, 'Usuarios')}</span>
                      </a>
                    </li>
                    )}
                    <li>
                      <a href="#" onClick={(e) => { e.preventDefault(); setShowConfig(true); }}>
                        <span><i className="fa-solid fa-gear" style={{ marginRight: 4 }} />{t(TXT.SETTINGS, 'Configuración')}</span>
                      </a>
                    </li>
                    <li>
                      <a href="#" onClick={(e) => { e.preventDefault(); setShowPwdChange(true); }}>
                        <span><i className="bi bi-key" style={{ marginRight: 4 }} />{t(TXT.CHANGE_PASSWORD, 'Cambio contraseña')}</span>
                      </a>
                    </li>
                    {(user?.functions || []).includes(36) && (
                    <li>
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate('/setup'); }}>
                        <span><i className="bi bi-gear" style={{ marginRight: 4 }} />{t(TXT.PARAMETRIZACION, 'Parametrización')}</span>
                      </a>
                    </li>
                    )}
                    <li><hr style={{ margin: '4px 0', borderColor: 'var(--color1, #70b9b0)', opacity: 0.3 }} /></li>
                    <li>
                      <a href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                        <span><i className="bi bi-box-arrow-right" style={{ marginRight: 4 }} />{t(TXT.SIGN_OUT, 'Cerrar sesión')}</span>
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
              <i
                className={`mobile-nav-toggle bi ${mobileNavOpen ? 'bi-x' : 'bi-list'}`}
                onClick={() => setMobileNavOpen(o => !o)}
              />
            </nav>
          )}
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* BUTTON BAR - mirrors main.asp #buttons div */}
      {/* ------------------------------------------------------------------ */}
      <div className="container mt-2">
        <div className="row">
          <div id="buttons" className="col-8">
            {/* Custom button bar (DocumentDetail) takes priority over default New button */}
            {buttonBar ? buttonBar : (onNewDocument && (
              <button
                className="button mt-2 mb-2 color1"
                id="btnnew"
                onClick={onNewDocument}
              >
                <i className="fa-solid fa-plus fa-1x color1"></i>&nbsp;&nbsp;{t(TXT.NEW, 'Nuevo')}&nbsp;&nbsp;
              </button>
            ))}
          </div>
          <div id="foundnew" className="col d-flex justify-content-end align-items-center">
            {summary}
          </div>
        </div>
        {user && <hr className="color1" style={{ borderTop: '2px solid' }} />}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* MAIN CONTENT */}
      {/* ------------------------------------------------------------------ */}
      <main className="main">
        <section id="services" className="services section">
          {sidebar ? (
            <div className="layout-sidebar-wrap">
              <div className="layout-sidebar-panel">{sidebar}</div>
              <div id="contenido" className="layout-sidebar-content">{children}</div>
            </div>
          ) : (
            <div id="contenido" className="container">
              {children}
            </div>
          )}
        </section>
      </main>

      {/* ------------------------------------------------------------------ */}
      {/* TOAST - mirrors #liveToastmsg */}
      {/* ------------------------------------------------------------------ */}
      <div className="toast-container position-fixed bottom-0 end-0 p-3">
        <div
          id="liveToastmsg"
          className={`toast ${toast.show ? 'show' : ''} ${{ success: 'bg-success text-white', error: 'bg-danger text-white', warning: 'bg-warning text-dark', info: '' }[toast.type] || ''}`}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="toast-body">{toast.message}</div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* MODALS */}
      {/* ------------------------------------------------------------------ */}
      <ConfirmModal
        show={showConfirm}
        message={confirmConfig.message}
        onConfirm={() => { setShowConfirm(false); confirmConfig.onConfirm?.(); }}
        onCancel={() => setShowConfirm(false)}
      />

      <PasswordChangeModal
        show={showPwdChange}
        onClose={() => setShowPwdChange(false)}
        showToast={showToast}
      />

      <ConfigModal
        show={showConfig}
        onClose={() => setShowConfig(false)}
        showToast={showToast}
      />

      <AssistantPanel />

      <a href="#" id="scroll-top" className="scroll-top d-flex align-items-center justify-content-center">
        <i className="bi bi-arrow-up-short"></i>
      </a>
    </>
  );
}
